import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  CreateProductTestMutation,
  ProductTest,
  ProductTestCreateFailure,
  ProductTestCreateInput,
  ProductTestMutationCreateArgs,
} from "@/graphql/codegen/graphql"
import { CreateProductTestDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCreateProductTestReturn {
  create: (input: ProductTestCreateInput) => Promise<ProductTest>
  isCreating: boolean
  error?: CombinedError
}

export const useCreateProductTest = (onSuccess?: () => void): UseCreateProductTestReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<CreateProductTestMutation, ProductTestMutationCreateArgs>(
    CreateProductTestDocument
  )

  const create = async (input: ProductTestCreateInput): Promise<ProductTest> => {
    const { data, error } = await executeMutation({ input })
    const output = data?.productTest.create
    const hasError = error || output?.__typename === "ProductTestCreateFailure"

    if (hasError) {
      const errorMessage =
        error?.message ?? (output as ProductTestCreateFailure).error.message ?? "Failed to create product test."
      notify({
        type: NotificationType.Error,
        message: errorMessage,
      })
      throw new Error(errorMessage)
    }

    onSuccess?.()
    return output!.productTest as ProductTest
  }

  return {
    create,
    isCreating: fetching,
    error,
  }
}
